import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { Suspense, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { setPlayerRateOnRoundAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/actions/setPlayerRateOnRound/setPlayerRateOnRoundAtom";
import { getRateFromRoundIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRateFromRoundIdAtom/getRatefromRoundIdAtom";
import { getRoundFromIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { getStartSlotIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getStartSlotId/getStartSlotIdAtom";
import { Loader } from "@whoosh/design-system/src/components/display/Loader";
import { Text } from "@whoosh/design-system/src/components/primitive";

import { GuestPlayerRateDisplayQuery } from "./__generated__/GuestPlayerRateDisplayQuery.graphql";

type GuestPlayerRateDisplayProps = {
  membershipTypeId: string | undefined;
  nonPlayingHost: boolean;
  roundId: string;
};

export const GuestPlayerRateDisplay = ({
  membershipTypeId,
  nonPlayingHost,
  roundId,
}: GuestPlayerRateDisplayProps) => {
  const [queryRef, loadQuery] = useQueryLoader<GuestPlayerRateDisplayQuery>(
    GuestPlayerRateDisplayQueryString
  );

  const startSlotId = useAtomValue(getStartSlotIdAtom);

  useEffect(() => {
    if (startSlotId && membershipTypeId !== "") {
      loadQuery(
        {
          timeSlotId: startSlotId ?? "",
          membershipTypeId,
          nonPlayingHost,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [loadQuery, membershipTypeId, nonPlayingHost, startSlotId]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <GuestRateDisplayNewInner queryRef={queryRef} roundId={roundId} />
      ) : null}
    </Suspense>
  );
};

type GuestRateDisplayNewInnerProps = {
  queryRef: PreloadedQuery<GuestPlayerRateDisplayQuery>;
  roundId: string;
};

export const GuestRateDisplayNewInner = ({
  queryRef,
  roundId,
}: GuestRateDisplayNewInnerProps) => {
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const currentRate = useAtomValue(getRateFromRoundIdAtom(roundId));
  const setPlayerRateOnRound = useSetAtom(setPlayerRateOnRoundAtom);

  const data = usePreloadedQuery(GuestPlayerRateDisplayQueryString, queryRef);

  const rates = useMemo(() => {
    return data.node?.playerRates || [];
  }, [data.node?.playerRates]);

  const ratesIncludeCurrentRate = rates.some(
    (r) => r?.rate?.id === currentRate?.id
  );

  useEffect(() => {
    if (
      (!currentRate && rates.length) ||
      (currentRate?.visibility !== "NONE" && !ratesIncludeCurrentRate)
    ) {
      const firstRate = rates[0]?.rate;
      if (firstRate) {
        setPlayerRateOnRound({
          roundId: currentRound?.localId || "",
          rate: { id: firstRate.id },
          isTargetingAll: false,
        });
      }
    }
  }, [
    currentRate,
    rates,
    ratesIncludeCurrentRate,
    setPlayerRateOnRound,
    currentRound?.localId,
  ]);

  const rateName = useMemo(() => {
    if (currentRate?.name) {
      return currentRate.name;
    }
    // Fallback: lookup by ID if name is missing
    const foundRate = rates.find((r) => r?.rate?.id === currentRate?.id);
    return foundRate?.rate?.name || "None";
  }, [currentRate?.name, currentRate?.id, rates]);

  return (
    <>
      <Text variant="mainTiny" color="text-secondary">{t`Guest Rate`}</Text>
      <Text variant="mainSmallSemiBold" color="text-primary">
        {rateName}
      </Text>
    </>
  );
};

const GuestPlayerRateDisplayQueryString = graphql`
  query GuestPlayerRateDisplayQuery(
    $timeSlotId: ID!
    $membershipTypeId: ID
    $nonPlayingHost: Boolean!
  ) {
    node(id: $timeSlotId) {
      ... on Slot {
        playerRates(
          type: GUEST
          membershipTypeId: $membershipTypeId
          nonPlayingHost: $nonPlayingHost
          visibleToMembershipTypeId: $membershipTypeId
        ) {
          rate {
            id
            name
            code
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
        }
      }
    }
  }
`;
